import { Component } from '@theme/component';
import { getScrollTop, getScrollEventTarget } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';
import EmblaCarousel from '@theme/embla-carousel';
import Autoplay from '@theme/embla-carousel-autoplay';
import { bindGrabCursor } from '@theme/be-embla-grab-cursor';

/** Scroll distance (px) after which the expanded card auto-collapses. */
const SCROLL_COLLAPSE_THRESHOLD = 100;

/**
 * A collapsible, auto-rotating carousel pinned to a corner of the viewport,
 * powered by the vendored Embla library (assets/embla-carousel.js) like the
 * theme's other carousels. Embla drives the swipe/drag, the seamless loop,
 * and — via the Autoplay plugin — the timed advance to the next card.
 *
 * Attributes:
 * - `autoplay` — seconds between automatic advances. Rotation pauses while
 *   the pointer hovers or focus sits inside the viewport, stops while the
 *   card is collapsed, and is suppressed entirely for visitors who prefer
 *   reduced motion. Omit to disable.
 *
 * The pagination dots page through the carousel and reflect the current
 * card, and the toggle collapses the card down to just its triangle (which
 * flips to point up so it can be expanded again). The card always starts
 * expanded and auto-collapses once the user scrolls down past a threshold
 * from where they started; toggling it manually disarms the auto-collapse
 * for that page view.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The overflow-hidden element Embla measures; its first child is the slide track.
 * @property {HTMLElement} [body] - The collapsible card body.
 * @property {HTMLButtonElement} [toggle] - The collapse/expand triangle.
 * @property {HTMLButtonElement[]} [dot] - The pagination dots.
 *
 * @extends {Component<Refs>}
 */
class FloatingCardsComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {ReturnType<typeof EmblaCarousel> | undefined} */
  #embla;

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {(() => void) | undefined} */
  #cancelAutoCollapse;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.#collapseOnScroll(this.#controller.signal);
    this.#initCarousel();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#destroyCarousel();
  }

  updatedCallback() {
    super.updatedCallback();

    // Section Rendering API / editor morphs can change slides or settings —
    // rebuild so Embla re-reads both the DOM and this element's attributes.
    this.#destroyCarousel();
    this.#initCarousel();
  }

  /** Collapses or expands the card. */
  toggle() {
    this.#cancelAutoCollapse?.();
    this.#setCollapsed(!this.classList.contains('be-floating-cards--collapsed'));
  }

  /**
   * Scrolls to the card whose pagination dot was clicked.
   *
   * @param {Event} event
   */
  select(event) {
    const dot = /** @type {HTMLElement} */ (event.target).closest('[data-index]');
    if (!(dot instanceof HTMLElement)) return;

    // An explicit choice restarts the rotation countdown from zero, so the
    // chosen card gets its full stay before autoplay moves on.
    this.#embla?.plugins().autoplay?.reset();
    this.#embla?.scrollTo(Number(dot.dataset.index));
  }

  /**
   * The auto-rotate interval in milliseconds, or 0 when disabled.
   * @returns {number}
   */
  get autoplayDelay() {
    const seconds = parseInt(this.getAttribute('autoplay') ?? '', 10);
    return Number.isNaN(seconds) ? 0 : seconds * 1000;
  }

  /**
   * Collapses the card once the user scrolls down past the threshold. The
   * baseline is taken from the first scroll event (tracking the lowest
   * position seen since), not from zero — scroll restoration on load fires a
   * programmatic scroll on the page container, and that jump must not count
   * as user scrolling. A manual toggle disarms it so it never fights an
   * explicit choice.
   *
   * @param {AbortSignal} signal
   */
  #collapseOnScroll(signal) {
    /** @type {number | undefined} */
    let baseline;

    const onScroll = () => {
      const position = getScrollTop();
      baseline = Math.min(baseline ?? position, position);
      if (position < baseline + SCROLL_COLLAPSE_THRESHOLD) return;
      this.#cancelAutoCollapse?.();
      this.#setCollapsed(true);
    };

    // On desktop (≥990px) the document root has overflow: hidden and
    // `.page-wrapper` is the scroll container, so `window` never emits scroll
    // events — listen on the real scroller (same approach as header.js).
    const scroller = getScrollEventTarget();
    scroller.addEventListener('scroll', onScroll, { passive: true, signal });
    this.#cancelAutoCollapse = () => {
      scroller.removeEventListener('scroll', onScroll);
      this.#cancelAutoCollapse = undefined;
    };
  }

  #initCarousel() {
    const { viewport } = this.refs;
    const slideCount = viewport.firstElementChild?.children.length ?? 0;

    // A lone card (or the editor's placeholder) has nothing to rotate or
    // drag — leave the static markup alone.
    if (slideCount < 2) return;

    const plugins = [];

    // Reduced-motion users still get a working carousel, just not one that
    // moves on its own.
    if (this.autoplayDelay > 0 && !prefersReducedMotion()) {
      plugins.push(
        Autoplay({
          delay: this.autoplayDelay,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
          stopOnFocusIn: true,
        })
      );
    }

    this.#embla = EmblaCarousel(
      viewport,
      {
        loop: true,
        // Let drag momentum carry past the adjacent snap and soften the
        // settle — see the note in be-embla-carousel.js.
        skipSnaps: true,
        duration: 30,
        // Slide spacing uses `margin-inline-end`; telling Embla the writing
        // direction keeps its physical margin measurement pointed at the
        // same edge on RTL storefronts.
        direction: document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr',
      },
      plugins
    );

    this.#embla.on('select', () => this.#updateActiveDot());
    this.#embla.on('reInit', () => this.#updateActiveDot());
    bindGrabCursor(this.#embla);
  }

  #destroyCarousel() {
    this.#embla?.destroy();
    this.#embla = undefined;
  }

  /**
   * @param {boolean} collapsed
   */
  #setCollapsed(collapsed) {
    this.classList.toggle('be-floating-cards--collapsed', collapsed);

    // No point rotating a hidden card — and expanding should grant the
    // visible card a fresh full interval, which play() provides.
    const autoplay = this.#embla?.plugins().autoplay;
    if (collapsed) autoplay?.stop();
    else autoplay?.play();

    const { toggle } = this.refs;
    if (toggle) {
      toggle.setAttribute('aria-expanded', String(!collapsed));
      const label = collapsed ? toggle.dataset.expandLabel : toggle.dataset.collapseLabel;
      if (label) toggle.setAttribute('aria-label', label);
    }
  }

  /** Marks the dot for Embla's selected snap as active. */
  #updateActiveDot() {
    const { dot: dots } = this.refs;
    if (!dots?.length || !this.#embla) return;

    const active = this.#embla.selectedScrollSnap();

    dots.forEach((currentDot, index) => {
      const isActive = index === active;
      currentDot.classList.toggle('is-active', isActive);
      if (isActive) {
        currentDot.setAttribute('aria-current', 'true');
      } else {
        currentDot.removeAttribute('aria-current');
      }
    });
  }
}

if (!customElements.get('floating-cards-component')) {
  customElements.define('floating-cards-component', FloatingCardsComponent);
}
